import React, { useReducer } from 'react';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import ReusableModal from '../../../components/ReusableModal/ReusableModal';
import styled from 'styled-components';
import AuthInput from './AuthInput';
import { Button, ErrorMessage } from 'playup-web-core';
import { createBankAccount, request2faCode } from '../../../api/withdraw';
import useNotify from '../../../hooks/useNotify';
import { parseApiError } from '../../../utils/parseApiError';

// TODO: This is a bad title
const TwoFAModal = (props: Props) => {
	const { user } = React.useContext(PortalSessionContext);
	const [errors, setErrors] = React.useState<APIError>();
	const notify = useNotify();

	const clampIndex = (index: number) => {
		if (index > 6) {
			return 6;
		} else if (index < 0) {
			return 0;
		}
		return index;
	};

	// ! This reducer is copied from WithdrawTwoFA.tsx in Portal App. Changes must be synced.
	const [{ userCode, focusedIndex, status }, dispatch] = useReducer(
		(state: any, action: any) => {
			switch (action.type) {
				case 'INPUT':
					return {
						...state,
						userCode: [
							...state.userCode.slice(0, Math.min(action.payload.index, 5)),
							action.payload.value,
							...state.userCode.slice(action.payload.index + 1),
						],
						focusedIndex: clampIndex(action.payload.index + 1),
					};
				case 'BACKSPACE':
					return {
						...state,
						userCode: [
							...state.userCode.slice(0, Math.min(action.payload.index, 5)),
							'',
							...state.userCode.slice(action.payload.index + 1),
						],
						focusedIndex: clampIndex(action.payload.index - 1),
					};
				case 'PASTE':
					return { ...state, userCode: state.userCode.map((_, index) => action.payload.value[index] || '') };
				case 'FOCUS':
					return { ...state, focusedIndex: clampIndex(action.payload.index) };
				case 'VERIFY':
					return { ...state, status: 'verifying' };
				case 'ERROR':
					return { ...state, status: 'idle' };
			}
		},
		{ userCode: Array(6).fill(''), focusedIndex: 0, status: 'idle' }
	);

	const valueFull = () => {
		if (userCode.length === 6) {
			const hasMissing = userCode.some((x) => x === '' || x === undefined || x === null || isNaN(Number(x)));
			return !hasMissing;
		}
		return false;
	};

	const submit = () => {
		createBankAccount({ ...props.bankData, token: userCode.join('') })
			.then((response) => {
				props.onClose();
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your bank account has been added',
					timeout: 5000,
				});
				props.onWithdrawlCreated();
			})
			.catch((error) => {
				setErrors(error);
				dispatch({ type: 'ERROR' });
			});
	};

	const handleInput = (index: number, value: string) => {
		dispatch({ type: 'INPUT', payload: { index, value } });
	};

	const handleBackspace = () => {
		dispatch({ type: 'BACKSPACE', payload: { index: focusedIndex } });
	};

	const handlePaste = (value: string) => {
		dispatch({ type: 'PASTE', payload: { value } });
		if (value.length === 6) {
			dispatch({ type: 'VERIFY' });
			submit();
		}
	};

	const handleFocus = (index: number) => {
		dispatch({ type: 'FOCUS', payload: { index } });
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		dispatch({ type: 'VERIFY' });
		submit();
	};

	if (user?.phone == null) {
		return null;
	}
	const maskedPhoneNumber = user?.phone.replace(/\w(?=\w{3})/g, '*');

	return (
		<ReusableModal title="Verification Required" onClose={props.onClose}>
			<PaddedCenteredText>
				Please enter the code we sent to {user?.calling_code || ''}
				{maskedPhoneNumber}
			</PaddedCenteredText>

			<form onSubmit={handleSubmit}>
				<InputGrid>
					{userCode.map((value, index) => {
						return (
							<AuthInput
								key={index}
								index={index}
								value={value}
								isFocused={focusedIndex === index}
								onInput={handleInput}
								onBackspace={handleBackspace}
								onPaste={handlePaste}
								onFocus={handleFocus}
								isDisabled={status === 'verifying'}
							/>
						);
					})}
				</InputGrid>
				{errors?.errors && <ErrorMessage>{parseApiError(errors?.errors as any)}</ErrorMessage>}
				<Button disabled={status === 'verifying' || !valueFull()} type="submit" kind="white">
					{status === 'verifying' ? 'Verifying...' : 'Submit'}
				</Button>
			</form>
			<PaddedCenteredText>It may take up to 2 minutes to receive your code.</PaddedCenteredText>
			<CenteredText>
				Didn't get the code? <ResendLink onClick={() => request2faCode({ ...props.bankData })}>RESEND</ResendLink>
			</CenteredText>
		</ReusableModal>
	);
};

// scourge to _ in our variable names. BE needs to Jsonize for us.
interface Props {
	onClose: () => void;
	bankData: {
		account_name: string;
		bank_id: string | null;
		swift_code: string | null;
		bank_name: string | null;
		account_number: string;
	};
	onWithdrawlCreated: () => void;
}

export default TwoFAModal;

const CenteredText = styled.div`
	text-align: center;
	width: 100%;
	color: black;
	font-size: 14px;
`;

const PaddedCenteredText = styled(CenteredText)`
	padding-top: 15px;
`;

const InputGrid = styled.div`
	padding-top: 15px;
	display: grid;
	gap: 10px;
	grid-template-columns: repeat(6, 1fr);
	margin-bottom: 10px;
`;

const ResendLink = styled.a`
	color: blue;
	cursor: pointer;
	text-decoration: underline;
`;
